import "./Footer.css";
import { Link, useLocation } from "react-router-dom";
import { getRoutePaths, getLangFromPath } from "../../utils/i18n";
import { useTranslations } from "../../utils/translations";
import { useLanguage } from "../../utils/LanguageContext";
import ArrowIcon from "../ArrowIcon/ArrowIcon";
import logoMarkInvert from "url:../../../assets/logo-mark-invert.svg";
import augustSanderPartner from "url:../../../assets/partner-logos/august-sander-schuke.png";
import bahamPartner from "url:../../../assets/partner-logos/baham.png";
import dataDramaPartner from "url:../../../assets/partner-logos/data-and-drama.png";
import euPartner from "url:../../../assets/partner-logos/eu.png";
import fitFrankfurtPartner from "url:../../../assets/partner-logos/fit-in-frankfurt.png";
import fuPartner from "url:../../../assets/partner-logos/fu.png";
import gamesConnectPartner from "url:../../../assets/partner-logos/games-connect.png";
import goethePartner from "url:../../../assets/partner-logos/gi.png";
import goetheMmbPartner from "url:../../../assets/partner-logos/gi-mmb.png";
import kaleidoskopPartner from "url:../../../assets/partner-logos/kaleidoskop.png";
import monetiquePartner from "url:../../../assets/partner-logos/monetique.png";
import preIntegrationPartner from "url:../../../assets/partner-logos/pre-integration.png";
import theWhizStorePartner from "url:../../../assets/partner-logos/thewhizstore.png";
import you9Partner from "url:../../../assets/partner-logos/you9.png";
import indafPartner from "url:../../../assets/partner-logos/indaf.png";
import averaPartner from "url:../../../assets/partner-logos/avera.svg";
import byovPartner from "url:../../../assets/partner-logos/byov.svg";

const PARTNER_LOGOS = [
  { src: goethePartner, alt: "Goethe-Institut" },
  { src: goetheMmbPartner, alt: "Goethe-Institut Max Mueller Bhavan" },
  { src: bahamPartner, alt: "Baham" },
  { src: preIntegrationPartner, alt: "Pre-Integration and Transition Management" },
  { src: euPartner, alt: "European Union" },
  { src: dataDramaPartner, alt: "Data and Drama" },
  { src: monetiquePartner, alt: "Monétique" },
  { src: fitFrankfurtPartner, alt: "fit in Frankfurt" },
  { src: kaleidoskopPartner, alt: "Kaleidoskop" },
  { src: gamesConnectPartner, alt: "Games Connect Africa" },
  { src: fuPartner, alt: "Freie Universität Berlin" },
  { src: augustSanderPartner, alt: "August Sander Schule" },
  { src: you9Partner, alt: "You9" },
  { src: theWhizStorePartner, alt: "TheWhizStore" },
  { src: indafPartner, alt: "INDAF" },
  { src: averaPartner, alt: "Avera Homes" },
  { src: byovPartner, alt: "byov." },
];

const Footer = () => {
  const { currentLang } = useLanguage();
  const { t } = useTranslations();
  const paths = getRoutePaths(currentLang);

  // Get service groups from translations
  const serviceGroupSlugs = {
    automation: `#${t('service', 'groups.automation.slug')}`,
    webDigital: `#${t('service', 'groups.webDigital.slug')}`,
    finance: `#${t('service', 'groups.finance.slug')}`,
    brandDesign: `#${t('service', 'groups.brandDesign.slug')}`
  };
  


  return (
    <footer className="footer">
      <ul className="footer-partners" data-label={t('footer', 'footerPartners', 'along the way / selected collaborators / some remain confidential.')} aria-label="Partners and collaborators">
        {PARTNER_LOGOS.map((partner) => (
          <li key={partner.src}>
            <img src={partner.src} alt={partner.alt} loading="lazy" decoding="async" />
          </li>
        ))}
      </ul>
      <div className="footer-inner">
        <div className="footer-left">
          <div className="footer-brand">
            <img className="footer-logo" src={logoMarkInvert} alt="" />
            <span className="footer-wordmark">thewhizkind</span>
          </div>
          <p>{t('footer', 'brandTagline', 'independent / creative / technology')}</p>
        </div>
        <nav className="footer-center" aria-label="Footer navigation">
          <p className="footer-column-label">{t('footer', 'navigate', '01 / navigate')}</p>
          <Link to={paths.services} className="footer-link">
            {t('footer', 'footerLinks.services', 'services')}
          </Link>
          <Link to={paths.lab} className="footer-link">
            {t('footer', 'footerLinks.lab', 'lab')}
          </Link>
          <Link to={paths.gallery} className="footer-link">
            {t('footer', 'footerLinks.gallery', 'gallery')}
          </Link>
          <Link to={paths.about} className="footer-link">
            {t('footer', 'footerLinks.about', 'about')}
          </Link>
          <Link to={paths.contact} className="footer-link">
            {t('footer', 'footerLinks.contact', 'contact')}
          </Link>
          <Link to={paths.principles} className="footer-link">
            {t('footer', 'footerLinks.principles', 'principles')}
          </Link>
        </nav>
        <nav className="footer-services" aria-label="Services">
          <p className="footer-column-label">{t('footer', 'capabilities', '02 / capabilities')}</p>
          <Link to={`${paths.services}${serviceGroupSlugs.brandDesign}`} className="footer-link">{t('footer', 'footerLinks.brandAndIdentity', 'branding & identity')}</Link>
          <Link to={`${paths.services}${serviceGroupSlugs.webDigital}`} className="footer-link">{t('footer', 'footerLinks.webDevelopment', 'web development')}</Link>
          <Link to={`${paths.services}${serviceGroupSlugs.brandDesign}`} className="footer-link">{t('footer', 'footerLinks.graphicDesign', 'graphic design')}</Link>
          <Link to={`${paths.services}${serviceGroupSlugs.automation}`} className="footer-link">{t('footer', 'footerLinks.aiAndAutomation', 'ai & automation')}</Link>
          <Link to={`${paths.services}${serviceGroupSlugs.brandDesign}`} className="footer-link">{t('footer', 'footerLinks.communication', 'communication')}</Link>
          <Link to={`${paths.services}${serviceGroupSlugs.webDigital}`} className="footer-link">{t('footer', 'footerLinks.digitalisation', 'digitalisation')}</Link>
          <Link to={`${paths.services}${serviceGroupSlugs.finance}`} className="footer-link">{t('footer', 'footerLinks.creativeDevelopment', 'creative development')}</Link>
          <Link to={`${paths.services}${serviceGroupSlugs.automation}`} className="footer-link">{t('footer', 'footerLinks.processOptimisation', 'process optimisation')}</Link>
        </nav>
        <div className="footer-right">
          <p className="footer-column-label">{t('footer', 'connect', '03 / connect')}</p>
          <div className="footer-socials" aria-label="Social links">
            <a
              className="footer-social"
              href="https://www.instagram.com/thewhizkind"
              target="_blank"
              rel="noreferrer"
              aria-label={t('footer', 'socialAriaLabels.instagram', 'thewhizkind on Instagram (opens in new tab)')}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M7 3h10a4 4 0 0 1 4 4v10a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4V7a4 4 0 0 1 4-4Zm0 2a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H7Zm5 3.5A4.5 4.5 0 1 1 7.5 13 4.5 4.5 0 0 1 12 8.5Zm0 2A2.5 2.5 0 1 0 14.5 13 2.5 2.5 0 0 0 12 10.5Zm5.25-2.65a1.05 1.05 0 1 1-1.05 1.05 1.05 1.05 0 0 1 1.05-1.05Z" />
              </svg>
            </a>
            <a
              className="footer-social"
              href="https://www.linkedin.com/in/prayrit-singh/"
              target="_blank"
              rel="noreferrer"
              aria-label={t('footer', 'socialAriaLabels.linkedin', 'Prayrit Singh on LinkedIn (opens in new tab)')}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M6.94 8.5H4v12h2.94v-12ZM5.47 3.5A1.73 1.73 0 1 0 7.2 5.23 1.72 1.72 0 0 0 5.47 3.5ZM20 13.2c0-3.24-1.73-4.75-4.05-4.75a3.5 3.5 0 0 0-3.18 1.75h-.04v-1.5H9.9c.04 1 .04 12 .04 12h2.94v-6.7c0-.36.03-.72.13-.98.29-.72.94-1.46 2.04-1.46 1.44 0 2.02 1.1 2.02 2.7v6.44H20Z" />
              </svg>
            </a>
            <a
              className="footer-social"
              href="https://discord.com/users/pray.rit"
              target="_blank"
              rel="noreferrer"
              aria-label={t('footer', 'socialAriaLabels.discord', 'Prayrit Singh on Discord (opens in new tab)')}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M20.32 4.37a17.5 17.5 0 0 0-4.3-1.34c-.19.34-.4.79-.55 1.15a16.6 16.6 0 0 0-4.94 0 8.9 8.9 0 0 0-.56-1.15 17.6 17.6 0 0 0-4.3 1.34C2.6 8.6 1.9 12.7 2.2 16.75a17.7 17.7 0 0 0 5.37 2.73c.44-.6.82-1.24 1.15-1.92a11.5 11.5 0 0 1-1.82-.88c.15-.11.3-.23.44-.35a12.6 12.6 0 0 0 10.66 0c.15.12.29.24.44.35-.58.35-1.19.64-1.83.88.33.68.71 1.32 1.15 1.92a17.6 17.6 0 0 0 5.37-2.73c.36-4.7-.78-8.76-3.31-12.38ZM9.68 14.3c-.9 0-1.63-.83-1.63-1.85 0-1.02.72-1.85 1.63-1.85.92 0 1.65.84 1.63 1.85 0 1.02-.71 1.85-1.63 1.85Zm5.64 0c-.9 0-1.63-.83-1.63-1.85 0-1.02.72-1.85 1.63-1.85.93 0 1.65.84 1.63 1.85 0 1.02-.7 1.85-1.63 1.85Z" />
              </svg>
            </a>
            <a
              className="footer-social"
              href="https://github.com/prayritsingh"
              target="_blank"
              rel="noreferrer"
              aria-label={t('footer', 'socialAriaLabels.github', 'Prayrit Singh on GitHub (opens in new tab)')}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M12 2A10 10 0 0 0 8.84 21.5c.5.08.66-.23.66-.5v-1.69c-2.77.6-3.36-1.34-3.36-1.34-.46-1.16-1.11-1.47-1.11-1.47-.91-.62.07-.6.07-.6 1 .07 1.53 1.03 1.53 1.03.89 1.52 2.34 1.08 2.91.83.09-.65.35-1.08.63-1.33-2.22-.25-4.55-1.11-4.55-4.92 0-1.09.39-1.98 1.03-2.68-.1-.25-.45-1.27.1-2.64 0 0 .84-.27 2.75 1.02a9.58 9.58 0 0 1 5 0c1.91-1.29 2.75-1.02 2.75-1.02.55 1.37.2 2.39.1 2.64.64.7 1.03 1.59 1.03 2.68 0 3.82-2.34 4.66-4.57 4.91.36.31.68.92.68 1.85v2.74c0 .27.16.59.67.5A10 10 0 0 0 12 2Z" />
              </svg>
            </a>
          </div>
          <div className="footer-legal">
            <Link to={paths.disclaimer} className="footer-link">
              {t('footer', 'legal.disclaimer', 'impressum / disclaimer')}
            </Link>
            <Link to={paths.privacy} className="footer-link">
              {t('footer', 'legal.privacy', 'datenschutzerklärung / privacy policy')}
            </Link>
            <Link to={paths.accessibility} className="footer-link">
              {t('footer', 'legal.accessibility', 'accessibility')}
            </Link>
          </div>
        </div>
      </div>
      <div className="footer-bottom">
        <span>{t('common', 'copyright', '© 2026 thewhizkind')}</span>
        <span>frankfurt am main <ArrowIcon direction="both" /> new delhi</span>
        <span className="footer-signature">{"<powered by thewhizkind>"}</span>
      </div>
    </footer>
  );
};

export default Footer;
